import React, { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Loader2, Hourglass } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", password: "" };

export function RegisterDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = (v) => { if (!v) { setForm(EMPTY); setError(""); setDone(false); } onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/register", form);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="register-dialog">
        <DialogHeader><DialogTitle>{t("register.title")}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-4 text-center" data-testid="register-success">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><Hourglass className="h-6 w-6" /></div>
            <p className="text-sm text-muted-foreground">{t("register.success")}</p>
            <button onClick={() => close(false)} data-testid="register-close" className="brand-btn rounded-lg px-4 py-2 text-sm font-medium">{t("register.ok")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>{t("register.name")}</Label><Input value={form.name} onChange={set("name")} data-testid="register-name" required /></div>
            <div className="space-y-2"><Label>{t("register.email")}</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="register-email" dir="ltr" required /></div>
            <div className="space-y-2"><Label>{t("register.phone")}</Label><Input value={form.phone} onChange={set("phone")} data-testid="register-phone" dir="ltr" /></div>
            <div className="space-y-2"><Label>{t("register.password")}</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="register-password" minLength={6} required /></div>
            <p className="text-xs text-muted-foreground">{t("register.note")}</p>
            {error && <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</div>}
            <DialogFooter>
              <button type="submit" disabled={busy} data-testid="register-submit" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("register.submit")}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
